import { useState, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Wallet, Copy, Check, Search, Plus, X, ChevronDown, ArrowRight } from 'lucide-react';
import { useEntity } from '../../contexts/EntityContext';
import { useDebounce } from '../../hooks/useDebounce.js';
import { api, Wallet as WalletType } from '../../lib/api';
import { LoadingState } from '../../components/ui/Spinner';
import { useToast } from '../../components/ui/Toast';
import { CreateWalletModal } from '../../components/cashier/CreateWalletModal';
import { WalletCard, WALLET_CATEGORIES, CATEGORY_ICONS, CATEGORY_LABELS, type WalletCategory } from '../../components/cashier/WalletCard';
import { isCashierEntityAllowed } from '../../components/cashier/constants';
import { Link } from 'react-router-dom';

interface WalletBalance {
  walletId: string;
  walletName: string;
  currency: string;
  balance: string;
}

export default function CashierWalletsPage() {
  const { t } = useTranslation('admin');
  const { t: tc } = useTranslation('common');
  const { currentEntity } = useEntity();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [copiedAddress, setCopiedAddress] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedWallet, setSelectedWallet] = useState<WalletType | null>(null);
  const [typeFilter, setTypeFilter] = useState<WalletCategory | 'all'>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);

  const debouncedSearch = useDebounce(searchQuery, 300);

  const copyToClipboard = (address: string, walletId: string) => {
    navigator.clipboard.writeText(address);
    setCopiedAddress(walletId);
    setTimeout(() => setCopiedAddress(null), 2000);
  };

  // Only fetch wallets if entity code is FNK
  const isAllowedEntity = isCashierEntityAllowed(currentEntity?.code);

  const { data, isLoading } = useQuery({
    queryKey: ['wallets', currentEntity?.id, debouncedSearch, typeFilter],
    queryFn: () =>
      api.admin.wallets.list(currentEntity!.id, {
        search: debouncedSearch?.trim() ? debouncedSearch.trim().toLowerCase() : undefined,
        type: typeFilter !== 'all' ? typeFilter : undefined,
      }),
    enabled: !!currentEntity && isAllowedEntity,
  });

  const { data: balancesData } = useQuery({
    queryKey: ['balances', currentEntity?.id],
    queryFn: () => api.reports.balances(currentEntity!.id),
    enabled: !!currentEntity && isAllowedEntity,
  });

  const { data: currencies } = useQuery({
    queryKey: ['currencies'],
    queryFn: () => api.admin.currencies.list(),
  });

  // No client-side filtering: list comes from API with search and type params only
  const walletList = data?.data || [];

  // Extract balances by wallet ID
  const balancesByWalletId = useMemo(() => {
    const map: Record<string, WalletBalance> = {};
    if (balancesData?.byType) {
      Object.values(balancesData.byType).forEach((wallets: any[]) => {
        wallets.forEach((w: any) => {
          if (w.walletId) {
            map[w.walletId] = {
              walletId: w.walletId,
              walletName: w.walletName,
              currency: w.currency,
              balance: w.balance,
            };
          }
        });
      });
    }
    return map;
  }, [balancesData]);

  if (!currentEntity) return null;

  return (
    <div className="min-h-screen bg-background p-4 md:p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Page Header */}
        <div className="flex justify-between items-center flex-col md:flex-row gap-4">
          <div className="flex flex-col gap-1">
            <h1 className="text-xl font-bold text-foreground m-0">{t('wallets.title')}</h1>
            <p className="text-sm text-foreground-muted m-0">Manage wallets and cards</p>
          </div>
          <button
            type="button"
            className="flex items-center gap-2 px-5 py-2.5 bg-ctp-blue text-white border-none rounded-lg text-sm font-semibold cursor-pointer transition-all hover:opacity-90 shadow-lg max-md:w-full max-md:justify-center"
            onClick={() => setShowCreateModal(true)}
          >
            <Plus size={18} />
            <span>New Account</span>
          </button>
        </div>

        {/* Search and Filter Bar */}
        <div className="flex gap-4 max-md:flex-col">
          <div className="relative flex-1 bg-surface rounded-xl border border-border shadow-sm">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-foreground-muted" />
            <input
              type="text"
              className="w-full py-3.5 pl-12 pr-4 border-none bg-transparent text-sm text-foreground outline-none placeholder:text-foreground-muted rounded-xl"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="relative min-w-[150px] max-md:min-w-full">
            <select
              className="w-full py-3.5 px-4 pr-10 bg-surface border border-border rounded-xl text-sm text-foreground cursor-pointer outline-none appearance-none transition-colors hover:border-foreground-muted focus:border-ctp-blue shadow-sm"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value as WalletCategory | 'all')}
            >
              <option value="all">All Types</option>
              {WALLET_CATEGORIES.map(cat => (
                <option key={cat} value={cat}>{CATEGORY_LABELS[cat]}</option>
              ))}
            </select>
            <ChevronDown size={16} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-foreground-muted pointer-events-none" />
          </div>
        </div>

        {/* Wallets Grid */}
        {isLoading ? (
          <div className="py-20 px-5 flex items-center justify-center">
            <LoadingState message={tc('actions.loading')} />
          </div>
        ) : walletList.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 px-5 text-foreground-muted gap-4">
            <Wallet size={48} />
            <p className="m-0 text-sm">{debouncedSearch ? 'No wallets found matching your search' : 'No wallets configured'}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {walletList.map((wallet) => (
              <WalletCard
                key={wallet.id}
                wallet={wallet}
                onCopyAddress={copyToClipboard}
                isCopied={copiedAddress === wallet.id}
                onClick={() => setSelectedWallet(wallet)}
                balance={balancesByWalletId[wallet.id]?.balance}
              />
            ))}

          </div>
        )}

        {/* New Account (Create Wallet) Modal */}
        {showCreateModal && currentEntity && (
          <CreateWalletModal
            entityId={currentEntity.id}
            currencies={currencies?.data || []}
            onClose={() => setShowCreateModal(false)}
            onSuccess={() => {
              queryClient.invalidateQueries({ queryKey: ['wallets'] });
              queryClient.invalidateQueries({ queryKey: ['balances'] });
              setShowCreateModal(false);
              toast.success('Account created');
            }}
          />
        )}

        {/* Wallet Detail Modal */}
        {selectedWallet && (
          <WalletDetailModal
            wallet={selectedWallet}
            balance={balancesByWalletId[selectedWallet.id]}
            onClose={() => setSelectedWallet(null)}
            onCopyAddress={copyToClipboard}
            isCopied={copiedAddress === selectedWallet.id}
            tc={tc}
          />
        )}
      </div>

      {/* Animation keyframes for modal */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

function WalletDetailModal({
  wallet,
  balance,
  onClose,
  onCopyAddress,
  isCopied,
  tc,
}: {
  wallet: WalletType;
  balance?: WalletBalance;
  onClose: () => void;
  onCopyAddress: (address: string, walletId: string) => void;
  isCopied: boolean;
  tc: (key: string) => string;
}) {
  const category = wallet.type as WalletCategory;

  const formatBalance = (value: string) => {
    const num = parseFloat(value);
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  };

  const iconColorClasses: Record<WalletCategory, string> = {
    cash: 'bg-ctp-green/15 text-ctp-green',
    bank: 'bg-ctp-mauve/15 text-ctp-lavender',
    crypto: 'bg-ctp-peach/15 text-ctp-yellow',
    payment_system: 'bg-ctp-blue/15 text-ctp-sky',
  };

  const badgeColorClasses: Record<WalletCategory, string> = {
    cash: 'bg-ctp-green/15 text-ctp-green',
    bank: 'bg-ctp-mauve/15 text-ctp-lavender',
    crypto: 'bg-ctp-peach/15 text-ctp-yellow',
    payment_system: 'bg-ctp-blue/15 text-ctp-sky',
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000] p-5 animate-[fadeIn_0.15s_ease-out]" onClick={onClose}>
      <div className="bg-surface rounded-2xl w-full max-w-[480px] max-h-[90vh] overflow-y-auto shadow-2xl border border-border animate-[slideUp_0.2s_ease-out]" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-start p-6 pb-5 border-b border-border">
          <div className="flex items-start gap-3.5">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${iconColorClasses[category]}`}>
              {CATEGORY_ICONS[category] || <Wallet size={24} />}
            </div>
            <div className="flex flex-col gap-1.5">
              <h2 className="text-lg font-semibold text-foreground m-0">{wallet.name}</h2>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[13px] text-foreground-muted">{wallet.currencyCode}</span>
                <span className={`inline-flex px-2 py-0.5 rounded text-[11px] font-medium uppercase ${badgeColorClasses[category]}`}>
                  {CATEGORY_LABELS[category] || wallet.type}
                </span>
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium ${wallet.isActive ? 'bg-ctp-green/15 text-ctp-green' : 'bg-ctp-red/15 text-ctp-red'}`}>
                  {wallet.isActive ? tc('status.active') : tc('status.inactive')}
                </span>
              </div>
            </div>
          </div>
          <button className="bg-transparent border-none p-2 cursor-pointer text-foreground-muted rounded-lg flex items-center justify-center transition-all hover:bg-surface-hover hover:text-foreground-subtle" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="p-6 flex flex-col gap-6">
          {/* Balance Section */}
          <div className="flex flex-col gap-2">
            <div className="text-[11px] font-semibold text-foreground-muted uppercase tracking-wide">Current Balance</div>
            <div className="flex items-baseline gap-2">
              {balance ? (
                <>
                  <span className="text-[32px] font-bold text-foreground max-md:text-[28px]">{formatBalance(balance.balance)}</span>
                  <span className="text-base font-medium text-foreground-muted">{wallet.currencyCode}</span>
                </>
              ) : (
                <span className="text-lg text-foreground-muted italic">N/A</span>
              )}
            </div>
          </div>

          {/* View Movements Button */}
          <Link
            to={`/movements?walletId=${wallet.id}&walletName=${encodeURIComponent(wallet.name)}`}
            className="flex items-center justify-center gap-2 px-4 py-2.5 bg-ctp-blue/10 text-ctp-blue border border-ctp-blue/20 rounded-lg text-sm font-medium no-underline transition-colors hover:bg-ctp-blue/20"
          >
            <ArrowRight size={16} />
            Посмотреть движения
          </Link>

          {/* Address Section */}
          <div className="flex flex-col gap-2">
            <div className="text-[11px] font-semibold text-foreground-muted uppercase tracking-wide">Address / Account Details</div>
            {wallet.address ? (
              <div className="flex items-center gap-3 bg-background p-3 px-3.5 rounded-lg border border-border">
                <span className="flex-1 font-mono text-[13px] text-foreground-subtle break-all">{wallet.address}</span>
                <button
                  className={`flex items-center gap-1.5 px-3 py-1.5 bg-surface border border-border rounded-md text-xs font-medium cursor-pointer transition-all flex-shrink-0 ${isCopied ? 'bg-ctp-green/15 border-ctp-green text-ctp-green' : 'text-foreground-subtle hover:bg-surface-hover hover:border-border-subtle'}`}
                  onClick={() => onCopyAddress(wallet.address!, wallet.id)}
                >
                  {isCopied ? <Check size={16} /> : <Copy size={16} />}
                  <span>{isCopied ? 'Copied!' : 'Copy'}</span>
                </button>
              </div>
            ) : (
              <div className="text-sm text-foreground-muted italic">No address configured</div>
            )}
          </div>

          {/* Description Section */}
          {wallet.description && (
            <div className="flex flex-col gap-2">
              <div className="text-[11px] font-semibold text-foreground-muted uppercase tracking-wide">Notes</div>
              <div className="text-sm text-foreground-subtle leading-relaxed bg-background p-3 px-3.5 rounded-lg">{wallet.description}</div>
            </div>
          )}

          {/* Wallet ID */}
          <div className="flex flex-col gap-2">
            <div className="text-[11px] font-semibold text-foreground-muted uppercase tracking-wide">Wallet ID</div>
            <div className="font-mono text-xs text-foreground-muted">{wallet.id}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
